import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Container,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  getStockSelectionLocksGrouped,
  getStockSelectionLockDetails,
  deleteStockSelectionLock,
  deleteAllStockSelectionLocks
} from "../../components/Api/GmsStockSelectionLock";
import { 
  hasPagePermissionWithAdmin,
  hasApprovePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

export default function StockLockManagementPage() {
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedLock, setSelectedLock] = useState(null);
  const [lockDetails, setLockDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [deleteLoadingRow, setDeleteLoadingRow] = useState(null);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsStockLockManagement');
  const hasApprovePermission = hasApprovePermissionWithAdmin(permissions, '/GmsStockLockManagement');

  useEffect(() => {
    if (hasPermission || isAdmin || hasApprovePermission) {
      loadLocks();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [page, pageSize, hasPermission, isAdmin, hasApprovePermission]);

  const loadLocks = async () => {
    setLoading(true);
    try {
      const response = await getStockSelectionLocksGrouped(page, pageSize);
      if (response.isSuccess) {
        setLocks(response.locks || []);
        setTotalCount(response.totalRecords || 0);
      } else {
        Swal.fire("Error", response.message || "Failed to load locks", "error");
      }
    } catch (error) {
      console.error("Error loading locks:", error);
      Swal.fire("Error", "Failed to load stock locks: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (lock) => {
    setSelectedLock(lock);
    setDetailsModal(true);
    setDetailsLoading(true);
    setLockDetails([]);
    
    try {
      const response = await getStockSelectionLockDetails(lock.temporaryFormUniqueId);
      if (response.isSuccess) {
        setLockDetails(response.details || []);
      } else {
        Swal.fire("Error", response.message || "Failed to load details", "error");
      }
    } catch (error) {
      console.error("Error loading lock details:", error);
      Swal.fire("Error", "Failed to load lock details: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDelete = async (temporaryFormUniqueId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will remove all locks for this form.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it!'
    });

    if (result.isConfirmed) {
      setDeleteLoadingRow(temporaryFormUniqueId);
      try {
        const response = await deleteStockSelectionLock(temporaryFormUniqueId);
        if (response.isSuccess) {
          Swal.fire("Success", response.message || "Locks removed successfully", "success");
          loadLocks();
        } else {
          Swal.fire("Error", response.message || "Failed to remove locks", "error");
        }
      } catch (error) {
        console.error("Error deleting locks:", error);
        Swal.fire("Error", "Failed to remove locks: " + (error.response?.data?.message || error.message), "error");
      } finally {
        setDeleteLoadingRow(null);
      }
    }
  };

  const handleDeleteAll = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will remove ALL stock selection locks. This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete all!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await deleteAllStockSelectionLocks();
        if (response.isSuccess) {
          Swal.fire("Success", response.message || `${response.deletedCount || 0} lock(s) deleted successfully`, "success");
          loadLocks();
        } else {
          Swal.fire("Error", response.message || "Failed to delete all locks", "error");
        }
      } catch (error) {
        console.error("Error deleting all locks:", error);
        Swal.fire("Error", "Failed to delete all locks: " + (error.response?.data?.message || error.message), "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (!hasPermission && !isAdmin && !hasApprovePermission) {
    return <div>No permission to view this page</div>;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Stock Lock Management" pageTitle="Settings & Administration" />
        
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0">
                Stock Lock Management ({totalCount} records)
              </h5>
              <Button
                color="danger"
                onClick={handleDeleteAll}
                disabled={loading || totalCount === 0}
              >
                {loading ? (
                  <>
                    <ClipLoader size={20} color="white" />{" "}
                    <span className="ms-2">Deleting...</span>
                  </>
                ) : (
                  "Delete All"
                )}
              </Button>
            </div>

            {loading && locks.length === 0 ? (
              <div className="text-center py-5">
                <ClipLoader size={50} color="#556ee6" />
                <p className="mt-3">Loading locks...</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table bordered>
                    <thead className="table-light">
                      <tr>
                        <th>Sr.No</th>
                        <th>Form ID</th>
                        <th>Transaction Type</th>
                        <th>User Name</th>
                        <th>Date & Time</th>
                        <th>Stock Count</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locks.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No locks found
                          </td>
                        </tr>
                      ) : (
                        locks.map((lock, index) => (
                          <tr key={lock.temporaryFormUniqueId}>
                            <td>{(page - 1) * pageSize + index + 1}</td>
                            <td>{lock.temporaryFormUniqueId}</td>
                            <td>{lock.transactionTypeName || 'Unknown'}</td>
                            <td>{lock.userName || 'N/A'}</td>
                            <td>{formatDateTime(lock.readDateAndTime)}</td>
                            <td>{lock.stockCount || 0}</td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleViewDetails(lock)}
                                >
                                  Details
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDelete(lock.temporaryFormUniqueId)}
                                  disabled={deleteLoadingRow === lock.temporaryFormUniqueId}
                                >
                                  {deleteLoadingRow === lock.temporaryFormUniqueId ? (
                                    <ClipLoader size={15} color="white" />
                                  ) : (
                                    "Remove"
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                {totalCount > pageSize && (
                  <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                       style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                    <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                      <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                      Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <Button
                        color="light"
                        size="sm"
                        disabled={page === 1}
                        onClick={() => handlePageChange(page - 1)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                          color: page === 1 ? '#6c757d' : '#495057'
                        }}
                      >
                        <i className="mdi mdi-chevron-left"></i>
                      </Button>
                      
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNum = index + 1;
                        const isActive = page === pageNum;
                        
                        return (
                          <Button
                            key={pageNum}
                            color={isActive ? "primary" : "light"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: isActive ? '#556ee6' : '#fff',
                              color: isActive ? '#fff' : '#495057'
                            }}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      
                      <Button
                        color="light"
                        size="sm"
                        disabled={page === totalPages}
                        onClick={() => handlePageChange(page + 1)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: page === totalPages ? '#f8f9fa' : '#fff',
                          color: page === totalPages ? '#6c757d' : '#495057'
                        }}
                      >
                        <i className="mdi mdi-chevron-right"></i>
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>

        {/* Details Modal */}
        <Modal isOpen={detailsModal} toggle={() => setDetailsModal(false)} size="lg">
          <ModalHeader toggle={() => setDetailsModal(false)}>
            Lock Details - {selectedLock?.temporaryFormUniqueId}
          </ModalHeader>
          <ModalBody>
            {detailsLoading ? (
              <div className="text-center py-5">
                <ClipLoader size={50} color="#556ee6" />
                <p className="mt-3">Loading details...</p>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <strong>Transaction Type:</strong> {selectedLock?.transactionTypeName || 'Unknown'}<br />
                  <strong>User:</strong> {selectedLock?.userName || 'N/A'}<br />
                  <strong>Date & Time:</strong> {selectedLock ? formatDateTime(selectedLock.readDateAndTime) : 'N/A'}<br />
                  <strong>Total Stocks:</strong> {lockDetails.length}
                </div>
                {lockDetails.length === 0 ? (
                  <p className="text-center text-muted">No details found</p>
                ) : (
                  <div className="table-responsive" style={{maxHeight: '400px', overflowY: 'auto'}}>
                    <Table bordered size="sm">
                      <thead className="table-light" style={{position: 'sticky', top: 0, zIndex: 10}}>
                        <tr>
                          <th>Product Name</th>
                          <th>Batch Number</th>
                          <th>Expiry Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lockDetails.map((detail, index) => (
                          <tr key={index}>
                            <td>{detail.productName || 'N/A'}</td>
                            <td>{detail.purchaseStock_BatchNo || 'N/A'}</td>
                            <td>
                              {detail.purchaseStock_ExpDate 
                                ? formatDateTime(detail.purchaseStock_ExpDate) 
                                : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setDetailsModal(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}









